import { yupResolver } from "@hookform/resolvers/yup";
import { useFilesValidations } from "common/hooks/useFilesValidations";
import { toFormData } from "common/utils/toFormData";
import { Form } from "components/Form/Form";
import { useRequestDependencies } from "entities/Request";
import { useCompanySwitchStore } from "modules/CompanySwitch/store/useCompanySwitchStore";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useUnsavedChangesPrompt } from "shared/hooks/useUnsavedChangesPrompt";
import { userStore } from "entities/User/store/userStore";
import { Button, Loader } from "ui";
import { uploadStatementApi } from "../../api";
import { uplodFormDefaultValues } from "../../constants/uplodFormDefaultValues";
import { getCardStatementFormValidationSchema } from "../../validations/getCardStatementFormValidationSchema";
import StatementUploadFormView from "./StatementUploadForm.view";
import { ROUTES } from "shared/config";

const StatementUploadFormContainer = (props) => {
  const cardFormat = props.cardFormat;
  const user = userStore((state) => state.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ! useFilesValidations deprecated
  // TODO remove after files handling logic unification
  const { filesValidationSchema, setFilesValidations } = useFilesValidations();
  const navigate = useNavigate();

  const { data: { dependencies, validations } = {}, isLoading } =
    useRequestDependencies(
      cardFormat === "corporate"
        ? "corporate_cards_statement_form"
        : "credit_card_statement_form",
      null
    );

  const cardStatementFormValidationSchema =
    getCardStatementFormValidationSchema({
      filesValidationSchema,
      dependencies,
      cardFormat,
    });

  const selectedCompany = useCompanySwitchStore(
    (state) => state.selectedCompany ?? ""
  );

  useEffect(() => {
    if (validations && dependencies) {
      setFilesValidations({
        files: {
          max_allowed_file_size_in_bytes:
            validations.max_allowed_file_size_in_bytes,
          attachments_allowed_file_types:
            validations.attachments_allowed_file_types.data,
          attachments_count_rules: {
            min: validations.attachments_count_rules.data,
            max: validations.attachments_count_rules.data,
          },
        },
      });
      reset({
        ...uplodFormDefaultValues,
        card_type: dependencies?.credit_card_types[0],
        company_code: selectedCompany?.code,
      });
    }
  }, [dependencies, validations]);

  const form = useForm({
    resolver: yupResolver(cardStatementFormValidationSchema),
    defaultValues: uplodFormDefaultValues,
  });

  const {
    reset,
    setValue,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    if (selectedCompany?.code) setValue("company_code", selectedCompany?.code);
  }, [selectedCompany?.code]);

  const onSubmit = async ({ file, ...data }) => {
    setIsSubmitting(true);

    uploadStatementApi(toFormData({ file: file[0]?.file, ...data })).then(
      (response) => {
        if (response?.status !== 200) return setIsSubmitting(false);

        toast.success("Statement was uploaded", { theme: "colored" });

        setTimeout(() => {
          if (props.cardFormat === "corporate") {
            navigate("/corporate-requests/user");
          } else {
            navigate("/card-requests/user");
          }
          setIsSubmitting(false);
        }, 2000);
      }
    );
  };

  useUnsavedChangesPrompt({
    showPrompt: isDirty,
    stepLinks:
      cardFormat === "corporate"
        ? ["/corporate-requests/user"]
        : ["/card-requests/user"],
  });

  if (isLoading) {
    return (
      <Loader
        variant="primary"
        size="md"
        className="position-absolute top-50 vw-100 justify-content-center"
      >
        Loading...
      </Loader>
    );
  }

  return (
    <Form form={form} onSubmit={onSubmit}>
      <StatementUploadFormView
        cardFormat={props.cardFormat}
        requestor={`${user.first_name} ${user.last_name}`}
        maxAllowedFileSize={validations?.max_allowed_file_size_in_bytes}
        allowedFilesTypes={validations?.attachments_allowed_file_types?.data}
        creditCardTypes={dependencies?.credit_card_types}
      >
        <>
          <span className="border-top mt-5"></span>
          <Button
            type="submit"
            className="d-flex mx-auto mt-5"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            Submit
          </Button>
        </>
      </StatementUploadFormView>
    </Form>
  );
};

export default StatementUploadFormContainer;
